import React, {useCallback, useEffect, useRef} from 'react'
import Map from 'ol/Map'
import View from 'ol/View'
import TileLayer from 'ol/layer/Tile'
import VectorLayer from 'ol/layer/Vector'
import ImageLayer from 'ol/layer/Image'
import VectorSource from 'ol/source/Vector'
import ImageStatic from 'ol/source/ImageStatic'
import OSM from 'ol/source/OSM'
import {Feature} from 'ol'
import {Point, Polygon} from 'ol/geom'
import {fromLonLat, transformExtent} from 'ol/proj'
import {Style, Fill, Stroke, Circle as CircleStyle} from 'ol/style'
import 'ol/ol.css'

export type SampleDepth = 'topsoil' | 'subsoil'

export interface SamplePoint {
    latitude: number
    longitude: number
    label?: string
    // Sample depth type for distinguishing topsoil/subsoil
    sampleDepth?: SampleDepth
    // Dynamic values keyed by field name (e.g., {pH: 6.5, calcium: 120})
    values?: Record<string, number | null>
}

// Predefined color scales for different value ranges
export type ColorScaleType = 'ph' | 'nutrient' | 'custom'

export interface ColorScale {
    type: ColorScaleType
    // For custom scales: array of [value, color] breakpoints
    breakpoints?: Array<{value: number; color: string}>
    // Min/max for gradient calculation
    min?: number
    max?: number
}

export interface GeoMapProps {
    // Boundary coordinates as {x, y} pairs (x = latitude, y = longitude from API)
    boundaryCoordinates?: Array<{x: number; y: number}>
    // Multiple boundary polygons (for multi-block display)
    multipleBoundaries?: Array<Array<{x: number; y: number}>>
    // Sample points to display on the map
    samplePoints?: SamplePoint[]
    // Map height - can be pixels (number) or CSS value (string like "60vh")
    height?: number | string
    // Optional callback when a point is clicked
    onPointClick?: (point: SamplePoint) => void
    // Optional callback when hovering over a point
    onPointHover?: (point: SamplePoint) => void
    // Optional callback when mouse leaves a point
    onPointLeave?: () => void
    // Label of point to highlight (for external hover control)
    highlightedPointLabel?: string | null
    // Field name to use for color-coding points (e.g., 'pH', 'calcium')
    colorByField?: string
    // Color scale configuration
    colorScale?: ColorScale
    // Whether to show subsoil indicators differently
    distinguishSubsoil?: boolean
    // Raster overlay URL (e.g., IDW interpolation PNG)
    rasterUrl?: string | null
    // Raster extent in [minLon, minLat, maxLon, maxLat] format (EPSG:4326)
    rasterExtent?: number[]
    // Whether to show the raster overlay
    showRaster?: boolean
}

// Default color scales for common use cases
const DEFAULT_PH_SCALE: ColorScale = {
    type: 'ph',
    breakpoints: [
        {value: 4.5, color: '#dc2626'}, // Red - very acidic
        {value: 5.5, color: '#f97316'}, // Orange - acidic
        {value: 6.0, color: '#eab308'}, // Yellow - slightly acidic
        {value: 6.5, color: '#84cc16'}, // Light green - optimal
        {value: 7.0, color: '#22c55e'}, // Green - neutral
        {value: 7.5, color: '#06b6d4'}, // Cyan - slightly alkaline
        {value: 8.5, color: '#3b82f6'}  // Blue - alkaline
    ]
}

const DEFAULT_NUTRIENT_SCALE: ColorScale = {
    type: 'nutrient',
    breakpoints: [
        {value: 0, color: '#dc2626'},   // Red - deficient
        {value: 25, color: '#f97316'},  // Orange - low
        {value: 50, color: '#eab308'},  // Yellow - moderate
        {value: 75, color: '#84cc16'},  // Light green - adequate
        {value: 100, color: '#22c55e'}  // Green - optimal
    ]
}

// Helper function to get color based on value and scale
const getColorForValue = (value: number | null | undefined, scale: ColorScale): string => {
    if (value === null || value === undefined) {
        return '#9ca3af' // Gray for missing values
    }

    const breakpoints = scale.breakpoints || []
    if (breakpoints.length === 0) {
        return '#537425' // Default secondary green
    }

    // Find the appropriate color based on breakpoints
    for (let i = 0; i < breakpoints.length; i++) {
        const bp = breakpoints[i]
        if (bp && value <= bp.value) {
            return bp.color
        }
    }

    // Value is above all breakpoints, return last color
    const lastBp = breakpoints[breakpoints.length - 1]
    return lastBp?.color || '#537425'
}

// Get the appropriate color scale based on type
export const getColorScale = (type: ColorScaleType): ColorScale => {
    switch (type) {
        case 'ph':
            return DEFAULT_PH_SCALE
        case 'nutrient':
            return DEFAULT_NUTRIENT_SCALE
        default:
            return DEFAULT_NUTRIENT_SCALE
    }
}

const GeoMap: React.FC<GeoMapProps> = ({
    boundaryCoordinates,
    multipleBoundaries,
    samplePoints = [],
    height = 400,
    onPointClick,
    onPointHover,
    onPointLeave,
    highlightedPointLabel,
    colorByField,
    colorScale,
    distinguishSubsoil = false,
    rasterUrl,
    rasterExtent,
    showRaster = true
}) => {
    const mapRef = useRef<HTMLDivElement>(null)
    const mapInstance = useRef<Map | null>(null)
    const boundarySourceRef = useRef<VectorSource | null>(null)
    const pointsSourceRef = useRef<VectorSource | null>(null)
    const rasterLayerRef = useRef<ImageLayer<any> | null>(null)
    const currentHoveredLabel = useRef<string | null>(null)

    // Create style for a point based on its value, depth, and highlight state
    const createPointStyle = useCallback((point: SamplePoint, isHighlighted: boolean = false): Style => {
        const isSubsoil = point.sampleDepth === 'subsoil'
        const baseRadius = isHighlighted ? 10 : (isSubsoil && distinguishSubsoil ? 6 : 8)

        // Determine fill color
        let fillColor = '#537425' // Default secondary green
        if (isSubsoil && distinguishSubsoil) {
            fillColor = '#9CA3AF' // Gray for subsoil
        } else if (colorByField && point.values) {
            const value = point.values[colorByField]
            const scale = colorScale || DEFAULT_NUTRIENT_SCALE
            fillColor = getColorForValue(value, scale)
        }

        // Highlighted points get a prominent green border
        const strokeColor = isHighlighted ? '#8ABE3C' : '#ffffff'
        const strokeWidth = isHighlighted ? 3 : 2

        return new Style({
            image: new CircleStyle({
                radius: baseRadius,
                fill: new Fill({color: fillColor}),
                stroke: new Stroke({
                    color: strokeColor,
                    width: strokeWidth
                })
            })
        })
    }, [colorByField, colorScale, distinguishSubsoil])

    // Initialize the map once
    useEffect(() => {
        if (!mapRef.current || mapInstance.current) return

        // Create boundary source and layer
        const boundarySource = new VectorSource()
        boundarySourceRef.current = boundarySource

        const boundaryLayer = new VectorLayer({
            source: boundarySource,
            style: new Style({
                stroke: new Stroke({
                    color: '#8ABE3C', // Primary green
                    width: 3
                }),
                fill: new Fill({
                    color: 'rgba(138, 190, 60, 0.1)' // Primary green with less transparency when raster is shown
                })
            }),
            zIndex: 2 // Above raster layer
        })

        // Create raster layer for IDW interpolation overlay (initially empty)
        const rasterLayer = new ImageLayer({
            opacity: 0.7,
            zIndex: 1 // Below boundary and points
        })
        rasterLayerRef.current = rasterLayer

        // Create points source and layer (style will be set per-feature)
        const pointsSource = new VectorSource()
        pointsSourceRef.current = pointsSource

        const pointsLayer = new VectorLayer({
            source: pointsSource,
            zIndex: 3 // Above raster and boundary
        })

        // Create the map
        const map = new Map({
            target: mapRef.current,
            layers: [
                new TileLayer({
                    source: new OSM(),
                    zIndex: 0
                }),
                rasterLayer,
                boundaryLayer,
                pointsLayer
            ],
            view: new View({
                center: fromLonLat([0, 0]),
                zoom: 2
            })
        })

        mapInstance.current = map

        // Cleanup on unmount
        return () => {
            map.setTarget(undefined)
            mapInstance.current = null
        }
    }, [])

    // Set up click and hover handlers for points
    useEffect(() => {
        const map = mapInstance.current
        if (!map) return

        const handleClick = (event: { pixel: number[] }) => {
            if (!onPointClick) return
            const feature = map.forEachFeatureAtPixel(event.pixel, (f) => f)
            if (feature) {
                const pointData = feature.get('data') as SamplePoint | undefined
                if (pointData) {
                    onPointClick(pointData)
                }
            }
        }

        // Handle hover over points - cursor and callbacks
        const handlePointerMove = (event: { pixel: number[] }) => {
            const feature = map.forEachFeatureAtPixel(event.pixel, (f) => f)
            const target = map.getTargetElement()

            if (feature) {
                const pointData = feature.get('data') as SamplePoint | undefined
                if (target) {
                    (target as HTMLElement).style.cursor = 'pointer'
                }
                // Only fire hover callback if we're hovering a new point
                if (pointData && pointData.label !== currentHoveredLabel.current) {
                    currentHoveredLabel.current = pointData.label || null
                    if (onPointHover) {
                        onPointHover(pointData)
                    }
                }
            } else {
                if (target) {
                    (target as HTMLElement).style.cursor = ''
                }
                // Fire leave callback if we were hovering something
                if (currentHoveredLabel.current !== null) {
                    currentHoveredLabel.current = null
                    if (onPointLeave) {
                        onPointLeave()
                    }
                }
            }
        }

        map.on('click', handleClick)
        map.on('pointermove', handlePointerMove)

        return () => {
            map.un('click', handleClick)
            map.un('pointermove', handlePointerMove)
        }
    }, [onPointClick, onPointHover, onPointLeave])

    // Update boundary when coordinates change
    useEffect(() => {
        const source = boundarySourceRef.current
        if (!source) return

        source.clear()

        // Collect all boundary polygons to render
        const allBoundaries: Array<Array<{x: number; y: number}>> = []

        if (multipleBoundaries && multipleBoundaries.length > 0) {
            allBoundaries.push(...multipleBoundaries)
        } else if (boundaryCoordinates && boundaryCoordinates.length > 0) {
            allBoundaries.push(boundaryCoordinates)
        }

        if (allBoundaries.length > 0) {
            for (const boundary of allBoundaries) {
                if (boundary.length < 3) continue

                // Convert coordinates to OpenLayers format [lon, lat] -> projected
                // API returns x = latitude, y = longitude (based on existing codebase patterns)
                const coords = boundary.map(c => fromLonLat([c.y, c.x]))

                // Close the polygon if not already closed
                const first = coords[0]
                const last = coords[coords.length - 1]
                if (first && last && (first[0] !== last[0] || first[1] !== last[1])) {
                    coords.push([...first])
                }

                const polygon = new Polygon([coords])
                const feature = new Feature({geometry: polygon})
                source.addFeature(feature)
            }

            // Fit view to combined extent of all boundaries
            if (mapInstance.current && source.getFeatures().length > 0) {
                const extent = source.getExtent()
                mapInstance.current.getView().fit(extent, {
                    padding: [50, 50, 50, 50],
                    maxZoom: 18
                })
            }
        }
    }, [boundaryCoordinates, multipleBoundaries])

    // Update sample points when they change (NOT on highlight change)
    useEffect(() => {
        const source = pointsSourceRef.current
        if (!source) return

        source.clear()

        const validPoints = samplePoints.filter(p =>
            p.latitude !== null &&
            p.longitude !== null &&
            !isNaN(p.latitude) &&
            !isNaN(p.longitude)
        )

        validPoints.forEach(point => {
            const feature = new Feature({
                geometry: new Point(fromLonLat([point.longitude, point.latitude])),
                data: point
            })
            feature.setStyle(createPointStyle(point, false))
            source.addFeature(feature)
        })

        // If no boundary but we have points, fit to points
        if ((!boundaryCoordinates || boundaryCoordinates.length === 0) && validPoints.length > 0) {
            if (mapInstance.current && source.getFeatures().length > 0) {
                const extent = source.getExtent()
                mapInstance.current.getView().fit(extent, {
                    padding: [50, 50, 50, 50],
                    maxZoom: 16
                })
            }
        }
    }, [samplePoints, boundaryCoordinates, createPointStyle])

    // Update highlight styles separately (without re-fitting the view)
    useEffect(() => {
        const source = pointsSourceRef.current
        if (!source) return

        source.getFeatures().forEach(feature => {
            const pointData = feature.get('data') as SamplePoint | undefined
            if (pointData) {
                const isHighlighted = highlightedPointLabel !== null && pointData.label === highlightedPointLabel
                feature.setStyle(createPointStyle(pointData, isHighlighted))
            }
        })
    }, [highlightedPointLabel, createPointStyle])

    // Update raster layer when URL or extent changes
    useEffect(() => {
        const rasterLayer = rasterLayerRef.current
        if (!rasterLayer) {
            return
        }

        if (!showRaster || !rasterUrl || !rasterExtent || rasterExtent.length !== 4) {
            // Hide raster layer by setting source to undefined
            rasterLayer.setSource(undefined as unknown as ImageStatic)
            return
        }

        // Transform extent from EPSG:4326 to EPSG:3857 (Web Mercator)
        const projectedExtent = transformExtent(rasterExtent, 'EPSG:4326', 'EPSG:3857')

        const source = new ImageStatic({
            url: rasterUrl,
            imageExtent: projectedExtent
        })

        rasterLayer.setSource(source)
    }, [rasterUrl, rasterExtent, showRaster])

    const heightStyle = typeof height === 'number' ? `${height}px` : height

    return (
        <div
            ref={mapRef}
            style={{width: '100%', height: heightStyle}}
            className="rounded-lg border border-gray-300"
        />
    )
}

export default GeoMap
